import React from 'react';
import { getColors } from '../theme';
import { useApp } from '../context/AppContext';
import { WifiOff, RefreshCw } from 'lucide-react';

// ============================================
// Reusable skeleton primitives (exported)
// ============================================

export const SkeletonBox = ({ className = '', style = {} }) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div 
      className={`animate-pulse rounded-md ${className}`}
      style={{ backgroundColor: colors.bgTertiary, ...style }}
    />
  );
};

export const SkeletonText = ({ width = '100%', height = '14px', className = '' }) => (
  <SkeletonBox className={className} style={{ width, height }} />
);

export const SkeletonCircle = ({ size = '40px' }) => (
  <SkeletonBox style={{ width: size, height: size, borderRadius: '50%' }} />
);

// ============================================
// Inline skeleton components for sections
// ============================================

// Skeleton for a single list item
export const SkeletonListItem = ({ showProgress = false }) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div 
      className="flex items-center gap-3 p-3 rounded-md"
      style={{ backgroundColor: colors.bgTertiary }}
    >
      <SkeletonCircle size="20px" />
      <div className="flex-1 flex flex-col gap-1.5">
        <SkeletonText width="65%" height="14px" />
        {showProgress && <SkeletonText width="100%" height="6px" />}
      </div>
    </div>
  );
};

// Skeleton for habits section content
export const HabitsSkeleton = ({ count = 3 }) => (
  <div className="flex flex-col gap-2">
    {[...Array(count)].map((_, i) => (
      <SkeletonListItem key={i} />
    ))}
  </div>
);

// Skeleton for tasks section content  
export const TasksSkeleton = ({ count = 2 }) => (
  <div className="flex flex-col gap-2">
    {[...Array(count)].map((_, i) => (
      <SkeletonListItem key={i} />
    ))}
  </div>
);

// Skeleton for goals section content
export const GoalsSkeleton = ({ count = 2 }) => (
  <div className="flex flex-col gap-2">
    {[...Array(count)].map((_, i) => (
      <SkeletonListItem key={i} showProgress />
    ))}
  </div>
);

// Skeleton for stat cards grid
export const StatsSkeleton = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  const StatCard = () => (
    <div 
      className="p-4 rounded-lg flex flex-col gap-2 animate-pulse"
      style={{ backgroundColor: colors.bgSecondary }}
    >
      <SkeletonText width="50%" height="12px" />
      <SkeletonText width="60%" height="24px" />
    </div>
  );
  
  return (
    <>
      <StatCard />
      <StatCard />
      <StatCard />
      <StatCard />
    </>
  );
};

// Skeleton for calendar content
export const CalendarSkeleton = () => (
  <div>
    <div className="grid grid-cols-7 gap-1 mb-2">
      {[...Array(7)].map((_, i) => (
        <SkeletonText key={i} width="100%" height="16px" />
      ))}
    </div>
    <div className="grid grid-cols-7 gap-1">
      {[...Array(35)].map((_, i) => (
        <div key={i} className="aspect-square">
          <SkeletonBox style={{ width: '100%', height: '100%' }} />
        </div>
      ))}
    </div>
  </div>
);

// Skeleton for daily quote
export const QuoteSkeleton = () => (
  <div className="flex flex-col gap-2">
    <SkeletonText width="85%" height="16px" />
    <SkeletonText width="35%" height="12px" />
  </div>
);

// ============================================
// Full page skeletons for each route
// ============================================

// Habits page skeleton - table with calendar header
export const HabitsPageSkeleton = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header with title and buttons */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <SkeletonText width="280px" height="32px" />
          <SkeletonText width="120px" height="16px" className="mt-2" />
        </div>
        <div className="flex gap-2">
          <SkeletonBox style={{ width: '100px', height: '40px' }} />
          <SkeletonBox style={{ width: '100px', height: '40px' }} />
        </div>
      </div>
      
      {/* Habits table skeleton */}
      <div 
        className="rounded-lg p-4 overflow-hidden"
        style={{ backgroundColor: colors.bgSecondary }}
      >
        {/* Table header */}
        <div className="flex items-center gap-4 mb-4 pb-3" style={{ borderBottom: `1px solid ${colors.border}` }}>
          <SkeletonText width="120px" height="16px" />
          <div className="flex-1 flex gap-2 overflow-hidden">
            {[...Array(7)].map((_, i) => (
              <SkeletonBox key={i} style={{ width: '48px', height: '48px', flexShrink: 0 }} />
            ))}
          </div>
        </div>
        
        {/* Habit rows */}
        {[...Array(5)].map((_, i) => (
          <div key={i} className="flex items-center gap-4 py-3">
            <div className="flex items-center gap-2" style={{ width: '120px' }}>
              <SkeletonCircle size="16px" />
              <SkeletonText width="80px" height="14px" />
            </div>
            <div className="flex-1 flex gap-2">
              {[...Array(7)].map((_, j) => (
                <SkeletonCircle key={j} size="24px" />
              ))}
            </div>
          </div>
        ))}
      </div>
      
      {/* Chart/heatmap skeleton */}
      <div 
        className="rounded-lg p-4"
        style={{ backgroundColor: colors.bgSecondary }}
      >
        <SkeletonText width="150px" height="20px" className="mb-4" />
        <SkeletonBox style={{ width: '100%', height: '200px' }} />
      </div>
    </div>
  );
};

// Tasks page skeleton
export const TasksPageSkeleton = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <SkeletonText width="200px" height="32px" />
          <SkeletonText width="80px" height="16px" className="mt-2" />
        </div>
        <div className="flex gap-2">
          <SkeletonBox style={{ width: '80px', height: '36px' }} />
          <SkeletonBox style={{ width: '100px', height: '36px' }} />
        </div>
      </div>
      
      {/* Filter bar */}
      <div className="flex gap-2">
        <SkeletonBox style={{ width: '100px', height: '32px' }} />
        <SkeletonBox style={{ width: '100px', height: '32px' }} />
      </div>
      
      {/* Chart */}
      <div 
        className="rounded-lg p-4"
        style={{ backgroundColor: colors.bgSecondary }}
      >
        <SkeletonBox style={{ width: '100%', height: '150px' }} />
      </div>
      
      {/* Task list */}
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <div 
            key={i}
            className="rounded-lg p-4 flex items-center gap-4"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <SkeletonCircle size="24px" />
            <div className="flex-1">
              <SkeletonText width="60%" height="16px" />
              <SkeletonText width="30%" height="12px" className="mt-2" />
            </div>
            <SkeletonBox style={{ width: '60px', height: '24px' }} />
          </div>
        ))}
      </div>
    </div>
  );
};

// Goals page skeleton
export const GoalsPageSkeleton = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <SkeletonText width="350px" height="32px" />
          <SkeletonText width="100px" height="16px" className="mt-2" />
        </div>
        <SkeletonBox style={{ width: '100px', height: '40px' }} />
      </div>
      
      {/* Tab bar */}
      <div className="flex gap-2">
        <SkeletonBox style={{ width: '120px', height: '40px' }} />
        <SkeletonBox style={{ width: '120px', height: '40px' }} />
      </div>
      
      {/* Goals grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div 
            key={i}
            className="rounded-lg p-5"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <SkeletonText width="70%" height="18px" />
                <SkeletonText width="40%" height="12px" className="mt-2" />
              </div>
              <SkeletonCircle size="20px" />
            </div>
            <SkeletonBox style={{ width: '100%', height: '8px' }} className="mt-3" />
            <div className="flex justify-between mt-2">
              <SkeletonText width="30px" height="12px" />
              <SkeletonText width="30px" height="12px" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Events/Calendar page skeleton
export const EventsPageSkeleton = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div className="space-y-6 animate-pulse">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <SkeletonText width="250px" height="32px" />
          <SkeletonText width="100px" height="16px" className="mt-2" />
        </div>
        <div className="flex gap-2">
          <SkeletonBox style={{ width: '80px', height: '36px' }} />
          <SkeletonBox style={{ width: '100px', height: '36px' }} />
        </div>
      </div>
      
      {/* View toggle and month nav */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <SkeletonBox style={{ width: '36px', height: '36px' }} />
          <SkeletonText width="120px" height="24px" />
          <SkeletonBox style={{ width: '36px', height: '36px' }} />
        </div>
        <div className="flex gap-2">
          <SkeletonBox style={{ width: '80px', height: '32px' }} />
          <SkeletonBox style={{ width: '80px', height: '32px' }} />
        </div>
      </div>
      
      {/* Calendar grid */}
      <div 
        className="rounded-lg p-4"
        style={{ backgroundColor: colors.bgSecondary }}
      >
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((_, i) => (
            <SkeletonText key={i} width="100%" height="16px" />
          ))}
        </div>
        
        {/* Calendar cells */}
        <div className="grid grid-cols-7 gap-2">
          {[...Array(35)].map((_, i) => (
            <div key={i} className="aspect-square p-2">
              <SkeletonBox style={{ width: '100%', height: '100%' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Settings page skeleton
export const SettingsPageSkeleton = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  const SettingsSection = ({ children }) => (
    <div 
      className="rounded-lg p-5"
      style={{ backgroundColor: colors.bgSecondary }}
    >
      {children}
    </div>
  );
  
  return (
    <div className="space-y-6 animate-pulse max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <SkeletonText width="150px" height="32px" />
        <SkeletonText width="200px" height="16px" className="mt-2" />
      </div>
      
      {/* Profile section */}
      <SettingsSection>
        <div className="flex items-center gap-4 mb-4">
          <SkeletonCircle size="60px" />
          <div>
            <SkeletonText width="150px" height="18px" />
            <SkeletonText width="200px" height="14px" className="mt-2" />
          </div>
        </div>
        <div className="space-y-3">
          <SkeletonBox style={{ width: '100%', height: '44px' }} />
          <SkeletonBox style={{ width: '100%', height: '44px' }} />
        </div>
      </SettingsSection>
      
      {/* Subscription section */}
      <SettingsSection>
        <SkeletonText width="120px" height="18px" className="mb-4" />
        <div className="flex gap-4">
          <SkeletonBox style={{ flex: 1, height: '150px' }} />
          <SkeletonBox style={{ flex: 1, height: '150px' }} />
        </div>
      </SettingsSection>
      
      {/* Stats section */}
      <SettingsSection>
        <SkeletonText width="100px" height="18px" className="mb-4" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="text-center">
              <SkeletonText width="50px" height="28px" className="mx-auto" />
              <SkeletonText width="80px" height="12px" className="mx-auto mt-2" />
            </div>
          ))}
        </div>
      </SettingsSection>
    </div>
  );
};


// Skeleton for events list  
export const EventsSkeleton = ({ count = 3 }) => (
  <div className="flex flex-col gap-2">
    {[...Array(count)].map((_, i) => (
      <SkeletonListItem key={i} />
    ))}
  </div>
);

// Skeleton for productivity score section
export const ProductivityScoreSkeleton = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div className="flex flex-col md:flex-row items-center gap-6 animate-pulse">
      {/* Score ring skeleton */}
      <div 
        className="flex-shrink-0 rounded-full"
        style={{ 
          width: '120px', 
          height: '120px', 
          backgroundColor: colors.bgTertiary,
          border: `10px solid ${colors.border}`
        }}
      />
      
      {/* Score breakdown skeleton */}
      <div className="flex-1 w-full">
        <div className="flex items-center gap-2 mb-4">
          <SkeletonCircle size="18px" />
          <SkeletonText width="130px" height="16px" />
        </div>
        
        <div className="space-y-3">
          {/* Habits bar */}
          <div>
            <div className="flex justify-between mb-1">
              <SkeletonText width="80px" height="14px" />
              <SkeletonText width="30px" height="14px" />
            </div>
            <SkeletonBox style={{ width: '100%', height: '8px' }} />
          </div>
          
          {/* Tasks bar */}
          <div>
            <div className="flex justify-between mb-1">
              <SkeletonText width="70px" height="14px" />
              <SkeletonText width="30px" height="14px" />
            </div>
            <SkeletonBox style={{ width: '100%', height: '8px' }} />
          </div>
          
          {/* Goals bar */}
          <div>
            <div className="flex justify-between mb-1">
              <SkeletonText width="75px" height="14px" />
              <SkeletonText width="30px" height="14px" />
            </div>
            <SkeletonBox style={{ width: '100%', height: '8px' }} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Full dashboard skeleton (for auth loading only)
export const DashboardSkeleton = () => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  return (
    <div 
      className="min-h-screen"
      style={{ backgroundColor: colors.bg }}
    >
      {/* Header placeholder */}
      <div 
        className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center justify-between px-4"
        style={{ backgroundColor: colors.bg }}
      >
        <SkeletonText width="80px" height="24px" />
        <div className="flex gap-3">
          <SkeletonCircle size="32px" />
          <SkeletonCircle size="32px" />
        </div>
      </div>
      
      {/* Main content */}
      <div className="pt-20 px-4 pb-8 max-w-6xl mx-auto">
        {/* Quote */}
        <div 
          className="mb-8 p-4 rounded-lg"
          style={{ backgroundColor: colors.bgSecondary }}
        >
          <QuoteSkeleton />
        </div>
        
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatsSkeleton />
        </div>
        
        {/* Main grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div 
            className="lg:col-span-2 p-6 rounded-lg"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <div className="flex items-center justify-between mb-6">
              <SkeletonText width="150px" height="24px" />
              <div className="flex gap-2">
                <SkeletonCircle size="32px" />
                <SkeletonCircle size="32px" />
              </div>
            </div>
            <CalendarSkeleton />
          </div>
          
          {/* Right column */}
          <div className="flex flex-col gap-6">
            <div 
              className="p-6 rounded-lg"
              style={{ backgroundColor: colors.bgSecondary }}
            >
              <div className="flex items-center justify-between mb-4">
                <SkeletonText width="80px" height="20px" />
              </div>
              <HabitsSkeleton count={3} />
            </div>
            
            <div 
              className="p-6 rounded-lg"
              style={{ backgroundColor: colors.bgSecondary }}
            >
              <div className="flex items-center justify-between mb-4">
                <SkeletonText width="60px" height="20px" />
              </div>
              <TasksSkeleton count={2} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sync indicator for header - shows sync/offline status
export const SyncIndicator = ({ isOnline, isSyncing }) => {
  const { isDark } = useApp();
  const colors = getColors(isDark);
  
  // Don't show anything when online and not syncing
  if (isOnline && !isSyncing) return null;
  
  return (
    <div 
      className="flex items-center gap-1.5 px-2 py-1 rounded-full text-xs"
      style={{ 
        backgroundColor: isOnline ? colors.bgTertiary : colors.error + '30',
        color: isOnline ? colors.textMuted : colors.error
      }}
    >
      {!isOnline ? (
        <>
          <WifiOff size={12} />
          <span>Offline</span>
        </>
      ) : isSyncing ? (
        <>
          <RefreshCw size={12} className="animate-spin" />
          <span>Syncing</span>
        </>
      ) : null}
    </div>
  );
};

export default DashboardSkeleton;
